import { motion } from 'framer-motion';
import moment from 'moment';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import {
	Button,
	ChevronDownIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	DatePicker,
	IconButton,
	Popover,
	tokens,
} from '@treams/treams-ui';

import useTalentReviewPath from '../hooks/useTalentReviewPath';
import { DATE_FORMAT, formatTalentReviewDay } from '../talentReviewDate';

const Navigation = styled(Flex)`
	align-items: center;
	flex-shrink: 0;
	gap: ${tokens.gap.inline}px;
`;

const Trigger = styled(Button)`
	&& {
		border-radius: ${tokens.radius.sm}px;
	}
`;

const LABEL_TRANSITION = { duration: 0.2, ease: 'easeInOut' } as const;

const LabelClip = styled(motion.span)`
	display: inline-flex;
	align-items: center;
	align-self: stretch;
	overflow: hidden;
	white-space: nowrap;
`;

const LabelText = styled.span`
	display: block;
	white-space: nowrap;
	line-height: ${tokens.typography.lineHeights.heading};
`;

/**
 * Date label that animates its width when the text changes ("Vandaag" →
 * "7 september 2026"), so the arrows on either side slide instead of jump.
 * Width is measured from the text and animated on a clipping wrapper.
 * @param props component props
 * @param props.children the label text
 */
function AnimatedLabel(props: { children: string }) {
	const textRef = useRef<HTMLSpanElement>(null);
	const [width, setWidth] = useState<number>();

	useLayoutEffect(() => {
		const element = textRef.current;
		if (!element) {
			return;
		}

		const measure = () => setWidth(Math.ceil(element.getBoundingClientRect().width));
		measure();

		const observer = new ResizeObserver(measure);
		observer.observe(element);
		return () => observer.disconnect();
	}, [props.children]);

	return (
		<LabelClip
			initial={false}
			animate={width === undefined ? undefined : { width }}
			transition={LABEL_TRANSITION}
		>
			<LabelText ref={textRef}>{props.children}</LabelText>
		</LabelClip>
	);
}

/**
 * Day navigation for the live grid. Only days with activity (a placement, a
 * submission, a member change, …) and today can be opened; the arrows step
 * between those days and the calendar marks them with a colored circle, every other day
 * is disabled. Opening a day reloads the grid as it was at the end of that day.
 * @param props component props
 * @param props.groupID group of the grid
 * @param props.templateID template of the grid
 * @param props.localDate the day currently shown
 * @param props.activity all activity of the grid, used to find the days with events
 */
export default function TalentReviewDateNavigation(props: {
	groupID: string;
	templateID: string;
	localDate: graphql.Scalars['LocalDate']['input'];
	activity: Pick<graphql.TalentReviewActivity, 'createdAt'>[];
}) {
	const { t, i18n } = useTranslation();
	const history = useHistory();
	const location = useLocation();
	const path = useTalentReviewPath();
	const [open, setOpen] = useState(false);

	const today = moment().format(DATE_FORMAT);

	// Days with at least one event, oldest first. Today is always included so
	// the live grid stays reachable.
	const eventDays = useMemo(() => {
		const days = new Set(props.activity.map((item) => moment(item.createdAt).format(DATE_FORMAT)));
		days.add(today);
		return [...days].filter((day) => day <= today).sort();
	}, [props.activity, today]);
	const eventDaySet = useMemo(() => new Set(eventDays), [eventDays]);
	const hasEvents = (date: Date) => eventDaySet.has(moment(date).format(DATE_FORMAT));

	const formatDay = (day: string) =>
		formatTalentReviewDay(
			day,
			today,
			{
				today: t('talentReviews.v2.date.today'),
				yesterday: t('talentReviews.v2.date.yesterday'),
				dayBeforeYesterday: t('talentReviews.v2.date.dayBeforeYesterday'),
			},
			i18n.language,
		);

	const previousDay = [...eventDays].reverse().find((day) => day < props.localDate);
	const nextDay = eventDays.find((day) => day > props.localDate);

	const goTo = (localDate: string) => {
		setOpen(false);

		if (localDate === props.localDate) {
			return;
		}

		history.push(
			path({
				groupID: props.groupID,
				templateID: props.templateID,
				localDate,
				search: location.search,
			}),
		);
	};

	return (
		<Navigation>
			{props.localDate !== today && (
				<Button
					type="primary"
					title={t('talentReviews.v2.date.today')}
					disableSuccess
					onClick={() => goTo(today)}
				/>
			)}
			<IconButton
				type="secondary"
				icon={<ChevronLeftIcon />}
				aria-label={t('talentReviews.v2.date.previousDay')}
				disabled={!previousDay}
				onClick={() => {
					if (previousDay) {
						goTo(previousDay);
					}
				}}
			/>
			<Popover.Target
				visible={open}
				horizontalAlign="right"
				onDismiss={() => setOpen(false)}
				contents={
					<DatePicker
						value={moment(props.localDate, DATE_FORMAT).toDate()}
						isDayHighlighted={hasEvents}
						isDayDisabled={(date) => !hasEvents(date)}
						onChange={(date) => {
							if (date) {
								goTo(moment(date).format(DATE_FORMAT));
							}
						}}
						onDismiss={() => setOpen(false)}
					/>
				}
			>
				<Trigger
					type="secondary"
					title={<AnimatedLabel>{formatDay(props.localDate)}</AnimatedLabel>}
					rightIcon={<ChevronDownIcon />}
					aria-haspopup="dialog"
					aria-expanded={open}
					onClick={() => setOpen((value) => !value)}
				/>
			</Popover.Target>
			<IconButton
				type="secondary"
				icon={<ChevronRightIcon />}
				aria-label={t('talentReviews.v2.date.nextDay')}
				disabled={!nextDay}
				onClick={() => {
					if (nextDay) {
						goTo(nextDay);
					}
				}}
			/>
		</Navigation>
	);
}
